import {
  AdditiveBlending,
  BoxGeometry,
  BufferAttribute,
  BufferGeometry,
  CatmullRomCurve3,
  Color,
  ConeGeometry,
  CylinderGeometry,
  DoubleSide,
  DynamicDrawUsage,
  Group,
  IcosahedronGeometry,
  InstancedMesh,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  Object3D,
  PlaneGeometry,
  Quaternion,
  SphereGeometry,
  TubeGeometry,
  Vector3,
} from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import type { Course, Decor, DecorKind } from '@thrasher/shared';
import { createCourseMaterials } from './materials.js';
import {
  chainLinkTexture,
  graffitiTexture,
  neonTexture,
  paintTexture,
  rng,
  softDotTexture,
  windowsTexture,
} from './textures.js';

const dummy = new Object3D();

function chunkGeometry(chunk: {
  positions: number[];
  normals: number[];
  uvs: number[];
  colors: number[];
  indices: number[];
}): BufferGeometry {
  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(new Float32Array(chunk.positions), 3));
  geometry.setAttribute('normal', new BufferAttribute(new Float32Array(chunk.normals), 3));
  geometry.setAttribute('uv', new BufferAttribute(new Float32Array(chunk.uvs), 2));
  geometry.setAttribute('color', new BufferAttribute(new Float32Array(chunk.colors), 3));
  const count = chunk.positions.length / 3;
  geometry.setIndex(
    new BufferAttribute(
      count > 65535 ? new Uint32Array(chunk.indices) : new Uint16Array(chunk.indices),
      1,
    ),
  );
  geometry.computeBoundingSphere();
  geometry.computeBoundingBox();
  return geometry;
}

function withColor(geometry: BufferGeometry, color: Color): BufferGeometry {
  const g = geometry.index ? geometry.toNonIndexed() : geometry;
  const count = g.getAttribute('position').count;
  const colors = new Float32Array(count * 3);
  for (let i = 0; i < count; i += 1) {
    colors[i * 3] = color.r;
    colors[i * 3 + 1] = color.g;
    colors[i * 3 + 2] = color.b;
  }
  g.setAttribute('color', new BufferAttribute(colors, 3));
  if (g.getAttribute('uv') === undefined)
    g.setAttribute('uv', new BufferAttribute(new Float32Array(count * 2), 2));
  return g;
}

function instanced(
  geometry: BufferGeometry,
  material: MeshStandardMaterial | MeshBasicMaterial,
  items: Decor[],
  transform: (d: Decor, m: Matrix4) => void,
  shadows = true,
): InstancedMesh {
  const mesh = new InstancedMesh(geometry, material, Math.max(1, items.length));
  const matrix = new Matrix4();
  items.forEach((d, i) => {
    transform(d, matrix);
    mesh.setMatrixAt(i, matrix);
  });
  mesh.count = items.length;
  mesh.castShadow = shadows;
  mesh.receiveShadow = shadows;
  mesh.instanceMatrix.needsUpdate = true;
  mesh.computeBoundingSphere();
  return mesh;
}

function placement(d: Decor, m: Matrix4, lift = 0): void {
  dummy.position.set(d.p[0], d.p[1] + lift, d.p[2]);
  dummy.rotation.set(0, d.yaw, 0);
  dummy.scale.setScalar(d.scale);
  dummy.updateMatrix();
  m.copy(dummy.matrix);
}

/** Streetlight: pole + curved arm + head; the head is emissive. */
function lampGeometry(): { body: BufferGeometry; head: BufferGeometry } {
  const pole = new CylinderGeometry(0.07, 0.11, 6.2, 8).translate(0, 3.1, 0);
  const arm = new CylinderGeometry(0.05, 0.05, 1.3, 6).rotateZ(Math.PI / 2).translate(0.6, 6.1, 0);
  const head = new BoxGeometry(0.7, 0.14, 0.34).translate(1.2, 6.02, 0);
  return { body: mergeGeometries([pole, arm])!, head };
}

function floodlightGeometry(): { body: BufferGeometry; head: BufferGeometry } {
  const parts: BufferGeometry[] = [];
  parts.push(new CylinderGeometry(0.22, 0.4, 16, 8).translate(0, 8, 0));
  parts.push(new BoxGeometry(3.4, 1.9, 0.3).translate(0, 16.6, 0.2));
  parts.push(new BoxGeometry(3.6, 0.12, 0.6).translate(0, 15.55, 0.2));
  const heads: BufferGeometry[] = [];
  for (let r = 0; r < 2; r += 1) {
    for (let c = 0; c < 4; c += 1) {
      heads.push(new BoxGeometry(0.62, 0.62, 0.12).translate(-1.2 + c * 0.8, 16.15 + r * 0.8, 0.4));
    }
  }
  return { body: mergeGeometries(parts)!, head: mergeGeometries(heads)! };
}

function palmGeometry(): { trunk: BufferGeometry; fronds: BufferGeometry } {
  const curve = new CatmullRomCurve3([
    new Vector3(0, 0, 0),
    new Vector3(0.3, 3, 0.1),
    new Vector3(0.9, 6.2, 0.2),
    new Vector3(1.6, 9, 0.3),
  ]);
  const trunk = new TubeGeometry(curve, 16, 0.22, 7, false);
  const fronds: BufferGeometry[] = [];
  const top = new Vector3(1.6, 9, 0.3);
  for (let i = 0; i < 9; i += 1) {
    const frond = new PlaneGeometry(0.9, 4.2, 1, 6);
    const pos = frond.getAttribute('position') as BufferAttribute;
    for (let v = 0; v < pos.count; v += 1) {
      const y = pos.getY(v) + 2.1;
      const droop = -0.12 * y * y;
      pos.setXYZ(v, pos.getX(v) * (1 - y / 5), droop, y);
    }
    frond.computeVertexNormals();
    frond.rotateY((i / 9) * Math.PI * 2);
    frond.rotateX(-0.1);
    frond.translate(top.x, top.y, top.z);
    fronds.push(frond);
  }
  fronds.push(new SphereGeometry(0.4, 8, 6).translate(top.x, top.y, top.z));
  return { trunk, fronds: mergeGeometries(fronds)! };
}

function treeGeometry(): { trunk: BufferGeometry; crown: BufferGeometry } {
  const trunk = new CylinderGeometry(0.12, 0.18, 2.2, 7).translate(0, 1.1, 0);
  const crowns = [
    new IcosahedronGeometry(1.4, 1).translate(0, 2.9, 0),
    new IcosahedronGeometry(1.0, 1).translate(0.8, 2.5, 0.3),
    new IcosahedronGeometry(1.1, 1).translate(-0.6, 3.4, -0.4),
  ];
  return { trunk, crown: mergeGeometries(crowns)! };
}

/**
 * Renders the course: textured surface chunks, instanced decor, fence, neon, graffiti,
 * and a distant city skyline. Everything static is built once.
 */
export class CourseView {
  public readonly group = new Group();
  /** Glow sprites that pulse subtly. */
  private readonly neon: MeshBasicMaterial[] = [];

  public constructor(course: Course) {
    const materials = createCourseMaterials();
    for (const [material, chunk] of course.render) {
      const mesh = new Mesh(chunkGeometry(chunk), materials[material]);
      mesh.castShadow =
        material !== 'grass' &&
        material !== 'asphalt' &&
        material !== 'pavers' &&
        material !== 'dirt';
      mesh.receiveShadow = true;
      mesh.name = `course:${material}`;
      this.group.add(mesh);
    }
    this.buildDecor(course);
    this.buildFence(course.halfExtent);
    this.buildSkyline(course.halfExtent);
  }

  public update(time: number): void {
    const flicker = 0.92 + Math.sin(time * 7.3) * 0.03 + Math.sin(time * 13.1) * 0.02;
    for (const material of this.neon) material.opacity = flicker;
  }

  private byKind(course: Course, kind: DecorKind): Decor[] {
    return course.decor.filter((d) => d.kind === kind);
  }

  private buildDecor(course: Course): void {
    const glowTexture = softDotTexture();
    const glowMaterial = new MeshBasicMaterial({
      map: glowTexture,
      color: new Color('#ffcf8a'),
      transparent: true,
      blending: AdditiveBlending,
      depthWrite: false,
      opacity: 0.9,
    });
    const glowGeometry = new PlaneGeometry(1, 1);
    const glows: { p: Vector3; size: number; color?: string }[] = [];

    const poleMaterial = new MeshStandardMaterial({
      color: '#3a3f48',
      roughness: 0.5,
      metalness: 0.8,
    });
    const lampHeadMaterial = new MeshStandardMaterial({
      color: '#2b2b2b',
      emissive: new Color('#ffd9a0'),
      emissiveIntensity: 3,
    });

    const lamps = this.byKind(course, 'lamp');
    const lamp = lampGeometry();
    this.group.add(instanced(lamp.body, poleMaterial, lamps, (d, m) => placement(d, m)));
    this.group.add(instanced(lamp.head, lampHeadMaterial, lamps, (d, m) => placement(d, m), false));
    for (const d of lamps) {
      const p = new Vector3(1.2, 5.9, 0)
        .applyAxisAngle(new Vector3(0, 1, 0), d.yaw)
        .add(new Vector3(...d.p));
      glows.push({ p, size: 3.2 });
    }

    const floods = this.byKind(course, 'floodlight');
    const flood = floodlightGeometry();
    const floodHeadMaterial = new MeshStandardMaterial({
      color: '#ffffff',
      emissive: new Color('#fff4e0'),
      emissiveIntensity: 5,
    });
    // Face each floodlight toward the park centre.
    for (const d of floods) d.yaw = Math.atan2(-d.p[0], -d.p[2]);
    this.group.add(instanced(flood.body, poleMaterial, floods, (d, m) => placement(d, m)));
    this.group.add(
      instanced(flood.head, floodHeadMaterial, floods, (d, m) => placement(d, m), false),
    );
    for (const d of floods) {
      const p = new Vector3(0, 16.55 * d.scale, 0.8)
        .applyAxisAngle(new Vector3(0, 1, 0), d.yaw)
        .add(new Vector3(...d.p));
      glows.push({ p, size: 9 * d.scale, color: '#fff2dc' });
    }

    const palms = this.byKind(course, 'palm');
    const palm = palmGeometry();
    const trunkMaterial = new MeshStandardMaterial({ color: '#7a5b3e', roughness: 0.95 });
    const frondMaterial = new MeshStandardMaterial({
      color: '#3f8a3a',
      roughness: 0.8,
      side: DoubleSide,
    });
    this.group.add(instanced(palm.trunk, trunkMaterial, palms, (d, m) => placement(d, m)));
    this.group.add(instanced(palm.fronds, frondMaterial, palms, (d, m) => placement(d, m)));

    const trees = this.byKind(course, 'tree');
    const tree = treeGeometry();
    const crownMaterial = new MeshStandardMaterial({
      color: '#4d8f3a',
      roughness: 0.85,
      flatShading: true,
    });
    this.group.add(instanced(tree.trunk, trunkMaterial, trees, (d, m) => placement(d, m)));
    this.group.add(instanced(tree.crown, crownMaterial, trees, (d, m) => placement(d, m)));

    const cones = this.byKind(course, 'cone');
    const coneGeometry = mergeGeometries([
      withColor(new ConeGeometry(0.22, 0.7, 12).translate(0, 0.38, 0), new Color('#ff6a1a')),
      withColor(
        new CylinderGeometry(0.155, 0.19, 0.12, 12).translate(0, 0.38, 0),
        new Color('#ffffff'),
      ),
      withColor(new BoxGeometry(0.5, 0.04, 0.5).translate(0, 0.02, 0), new Color('#ff6a1a')),
    ])!;
    const vertexMaterial = new MeshStandardMaterial({ vertexColors: true, roughness: 0.6 });
    this.group.add(instanced(coneGeometry, vertexMaterial, cones, (d, m) => placement(d, m)));

    const barrels = this.byKind(course, 'barrel');
    const barrelGeometry = new CylinderGeometry(0.3, 0.3, 0.9, 14).translate(0, 0.45, 0);
    const barrelMaterial = new MeshStandardMaterial({
      color: '#2f6fb3',
      roughness: 0.55,
      metalness: 0.4,
    });
    this.group.add(instanced(barrelGeometry, barrelMaterial, barrels, (d, m) => placement(d, m)));

    for (const d of this.byKind(course, 'graffiti')) {
      const w = d.w ?? 4;
      const h = d.h ?? 2;
      const material = new MeshStandardMaterial({
        map: graffitiTexture(d.seed ?? 1, w / h),
        transparent: true,
        roughness: 0.7,
        polygonOffset: true,
        polygonOffsetFactor: -2,
        polygonOffsetUnits: -2,
        depthWrite: false,
      });
      const mesh = new Mesh(new PlaneGeometry(w, h), material);
      mesh.position.set(d.p[0], d.p[1], d.p[2]);
      mesh.rotation.y = d.yaw;
      mesh.receiveShadow = true;
      this.group.add(mesh);
    }

    for (const d of this.byKind(course, 'paint')) {
      const size = d.w ?? 10;
      const material = new MeshStandardMaterial({
        map: paintTexture(d.seed ?? 1),
        transparent: true,
        opacity: 0.85,
        roughness: 0.8,
        polygonOffset: true,
        polygonOffsetFactor: -3,
        polygonOffsetUnits: -3,
        depthWrite: false,
      });
      const mesh = new Mesh(new PlaneGeometry(size, size), material);
      mesh.rotation.set(-Math.PI / 2, 0, d.yaw);
      mesh.position.set(d.p[0], d.p[1], d.p[2]);
      mesh.receiveShadow = true;
      mesh.renderOrder = 1;
      this.group.add(mesh);
    }

    for (const d of this.byKind(course, 'sign')) {
      const w = d.w ?? 10;
      const h = d.h ?? 3;
      const board = new Mesh(
        new BoxGeometry(w + 0.6, h + 0.4, 0.3),
        new MeshStandardMaterial({ color: '#15171d', roughness: 0.6, metalness: 0.5 }),
      );
      board.position.set(d.p[0], d.p[1] + h / 2, d.p[2]);
      board.rotation.y = d.yaw;
      board.castShadow = true;
      this.group.add(board);
      for (const [side, color] of [
        [1, '#ff3d7f'],
        [-1, '#35d6ff'],
      ] as const) {
        const material = new MeshBasicMaterial({
          map: neonTexture(d.text ?? 'THRASHER', color),
          transparent: true,
          blending: AdditiveBlending,
          depthWrite: false,
          color: new Color(3, 3, 3),
        });
        this.neon.push(material);
        const neon = new Mesh(new PlaneGeometry(w, h * 0.95), material);
        neon.position
          .copy(board.position)
          .add(new Vector3(0, 0, side * 0.17).applyAxisAngle(new Vector3(0, 1, 0), d.yaw));
        neon.rotation.y = d.yaw + (side < 0 ? Math.PI : 0);
        this.group.add(neon);
      }
    }

    // Additive glow billboards (camera-facing via onBeforeRender).
    const glowMesh = new InstancedMesh(glowGeometry, glowMaterial, glows.length);
    glowMesh.instanceMatrix.setUsage(DynamicDrawUsage);
    glowMesh.frustumCulled = false;
    const quaternion = new Quaternion();
    const matrix = new Matrix4();
    const scaleVector = new Vector3();
    glowMesh.onBeforeRender = (_renderer, _scene, camera) => {
      quaternion.copy(camera.quaternion);
      glows.forEach((g, i) => {
        scaleVector.setScalar(g.size);
        matrix.compose(g.p, quaternion, scaleVector);
        glowMesh.setMatrixAt(i, matrix);
      });
      glowMesh.instanceMatrix.needsUpdate = true;
    };
    glowMesh.renderOrder = 2;
    this.group.add(glowMesh);
  }

  private buildFence(half: number): void {
    const texture = chainLinkTexture();
    const material = new MeshStandardMaterial({
      map: texture,
      alphaTest: 0.5,
      transparent: false,
      side: DoubleSide,
      metalness: 0.6,
      roughness: 0.5,
    });
    const height = 6;
    for (const [x, z, yaw] of [
      [0, -half, 0],
      [0, half, Math.PI],
      [-half, 0, Math.PI / 2],
      [half, 0, -Math.PI / 2],
    ] as const) {
      const length = half * 2;
      const geometry = new PlaneGeometry(length, height);
      const uv = geometry.getAttribute('uv') as BufferAttribute;
      for (let i = 0; i < uv.count; i += 1)
        uv.setXY(i, uv.getX(i) * (length / 0.35), uv.getY(i) * (height / 0.35));
      const mesh = new Mesh(geometry, material);
      mesh.position.set(x, height / 2, z);
      mesh.rotation.y = yaw;
      mesh.castShadow = true;
      this.group.add(mesh);
    }
  }

  private buildSkyline(half: number): void {
    const random = rng(777);
    const facades = [windowsTexture(1), windowsTexture(2)];
    const boxes: BufferGeometry[][] = [[], []];
    for (let i = 0; i < 90; i += 1) {
      const angle = (i / 90) * Math.PI * 2 + random() * 0.05;
      const distance = half + 380 + random() * 420;
      const w = 26 + random() * 44;
      const d = 26 + random() * 44;
      const h = 20 + random() * random() * 140;
      const geometry = new BoxGeometry(w, h, d);
      // World-scaled UVs so windows stay a consistent size.
      const uv = geometry.getAttribute('uv') as BufferAttribute;
      const normal = geometry.getAttribute('normal') as BufferAttribute;
      for (let v = 0; v < uv.count; v += 1) {
        const across = Math.abs(normal.getX(v)) > 0.5 ? d : w;
        uv.setXY(v, (uv.getX(v) * across) / 60, (uv.getY(v) * h) / 110);
        if (Math.abs(normal.getY(v)) > 0.5) uv.setXY(v, 0.02, 0.02);
      }
      geometry.rotateY(-angle);
      geometry.translate(Math.cos(angle) * distance, h / 2 - 2, Math.sin(angle) * distance);
      boxes[i % 2]!.push(geometry);
    }
    boxes.forEach((list, index) => {
      const facade = facades[index]!;
      const material = new MeshStandardMaterial({
        map: facade.map,
        emissiveMap: facade.emissive,
        emissive: new Color('#ffffff'),
        emissiveIntensity: 1.4,
        color: new Color('#4d5068'),
        roughness: 0.6,
        metalness: 0.3,
        envMapIntensity: 0.5,
      });
      const mesh = new Mesh(mergeGeometries(list)!, material);
      this.group.add(mesh);
    });
    // Rolling hills silhouette beyond the city.
    const segments = 128;
    const positions: number[] = [];
    const indices: number[] = [];
    for (let i = 0; i <= segments; i += 1) {
      const angle = (i / segments) * Math.PI * 2;
      const height =
        70 + Math.sin(angle * 3) * 40 + Math.sin(angle * 7 + 1) * 28 + Math.sin(angle * 13) * 12;
      const r = 1150;
      positions.push(Math.cos(angle) * r, -5, Math.sin(angle) * r);
      positions.push(Math.cos(angle) * r, height, Math.sin(angle) * r);
      if (i < segments) {
        const a = i * 2;
        indices.push(a, a + 2, a + 1, a + 1, a + 2, a + 3);
      }
    }
    const hills = new BufferGeometry();
    hills.setAttribute('position', new BufferAttribute(new Float32Array(positions), 3));
    hills.setIndex(indices);
    hills.computeVertexNormals();
    const hillMesh = new Mesh(
      hills,
      new MeshStandardMaterial({ color: '#6a5a78', roughness: 1, side: DoubleSide }),
    );
    this.group.add(hillMesh);
    // Outer ground ring so the world does not end at the fence (the park keeps its own floor).
    const outerMaterial = new MeshStandardMaterial({ color: '#3d4436', roughness: 1 });
    const far = 1200;
    for (const [x, z, w, d] of [
      [0, -(half + far) / 2 - 0.5, far * 2, far - half],
      [0, (half + far) / 2 + 0.5, far * 2, far - half],
      [-(half + far) / 2 - 0.5, 0, far - half, half * 2 + 2],
      [(half + far) / 2 + 0.5, 0, far - half, half * 2 + 2],
    ] as const) {
      const outer = new Mesh(new PlaneGeometry(w, d), outerMaterial);
      outer.rotation.x = -Math.PI / 2;
      outer.position.set(x, -0.02, z);
      this.group.add(outer);
    }
  }
}
